import { useState, useEffect, useRef } from "react";
import { styled, createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import MuiDrawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Container from "@mui/material/Container";
import MenuIcon from "@mui/icons-material/Menu";
import Sidebar from "./Sidebar";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { AppBar, Button, Link } from "@mui/material";
import SessionService from "../services/SessionService";
import { User } from "../models/User";
import { CSSDefaults, ROUTES_WITH_PERMISSIONS, SHAREPOINT_DOCUMENTS_URL, SHAREPOINT_LEAGO_USER_MANUAL_PATH } from "../models/GlobalConstants";
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import Notifications from "./Notifications";
import { useLocation } from "react-router-dom";
import api from "../services/ApiService";
import { ApiResponse } from "../models/ApiResponse";
import { SpecialTag } from "../models/SpecialTag";
import { SpecialTags } from "../models/Enum";
import { useAppDispatch, useAppSelector } from "../store";
import { setDashboardViewReviewRequiredTagId } from "../reducers/filtersReducer";
import { updateDashboardListRequest } from "../reducers/itemsReducer";
import ManageNotification from "./ManageNotification";
import { toast } from "react-toastify";
import { disableLoader, enableLoader } from "../reducers/globalLoadingReducer";
import SignalRConnector from "../services/SignalRConnector";
import { runExclusiveExportJobDownload } from "../utils/exportJobDownloadCoordinator";

type Props = {
  children?: React.ReactNode;
};

// Augment the palette to include a salmon color
declare module '@mui/material/styles' {
  interface Palette {
    purpleHues: Palette['primary'];
    greenHues: Palette['primary'];
  }

  interface PaletteOptions {
    purpleHues?: PaletteOptions['primary'];
    greenHues?: PaletteOptions['primary'];
  }
}

// Update the Button's color options to include a salmon option
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    purpleHues: true;
    greenHues: true;
  }
}

let defaultTheme = createTheme({
  // Theme customization goes here as usual, including tonalOffset and/or
  // contrastThreshold as the augmentColor() function relies on these
});

defaultTheme = createTheme({
  palette: {
    primary: {
      main: CSSDefaults.primaryColor,
    },
    secondary: {
      main: CSSDefaults.primaryColor,
    },
    purpleHues: defaultTheme.palette.augmentColor({
      color: {
        main: CSSDefaults.purpleHues1,
      },
      name: 'purpleHues1',
    }),
    greenHues: defaultTheme.palette.augmentColor({
      color: {
        main: CSSDefaults.greenHues1,
        light: CSSDefaults.greenHues2,
      },
      name: 'greenHues1',
    })
  },
  typography: {
    h6: { fontSize: "1rem" },
    allVariants: {
      color: "#000",
    },
  },
});

// Defined the drawer width for different breakpoints
const drawerWidth = {
  default: 340,
  tablet: 265,
};

function Copyright(props: any) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
    >
      {"Copyright © "}
      <Link color="inherit" href="https://www.visualcomfort.com/">
        Visual Comfort
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

// Styled component for Drawer
const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth.default,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    ...(!open && {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up("sm")]: {
        width: `calc(${theme.spacing(7)} + 1px)`,
      },
    }),
    [theme.breakpoints.between('sm', 'md')]: {
      width: drawerWidth.tablet,
      ...(!open && {
        width: theme.spacing(7),
        [theme.breakpoints.up("sm")]: {
          width: `calc(${theme.spacing(7)} + 1px)`,
        },
      }),
    },
  },
}));

export const PageLayout: React.FC<Props> = ({ children }) => {
  const [open, setOpen] = useState(true);
  const [user, setUser] = useState<User>();
  const [breadCrumbText, setBreadCrumbText] = useState("");
  const location = useLocation(); 
  const dispatch = useAppDispatch();

  const sessionService = SessionService.getInstance();
  const dashboardListRequest = useAppSelector((state) => state.items.dashboardListRequest);
  const processedExportJobsRef = useRef<Set<string>>(new Set());

  const downloadBlob = (blobData: Blob, fileName: string) => {
    const blobURL = window.URL.createObjectURL(blobData);
    const a = document.createElement('a');
    a.href = blobURL;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.download = fileName;
    a.click();
    a.remove();
    URL.revokeObjectURL(blobURL);
  };

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const logout = () => {
    sessionService.clearSession();
    sessionService.clearReduxState();
    window.location.replace("/");
  };

  useEffect(() => {
    const user = sessionService.getCurrentUser();
    setUser(user);
    fetchSpecialTags();
  }, []);

  // Global listener so export downloads work even if user navigates away
  useEffect(() => {
    const hub = SignalRConnector();

    const signalREventHandlers = {
      exportJobUpdated: async (event: any) => {
        const parsedEvent: any =
          typeof event === 'string'
            ? (() => {
                try {
                  return JSON.parse(event);
                } catch {
                  return {};
                }
              })()
            : event;

        const payload: any = Array.isArray(parsedEvent?.arguments) ? parsedEvent.arguments[0] : parsedEvent;

        const jobIdRaw = payload?.jobId ?? payload?.JobId;
        const statusRaw = payload?.status ?? payload?.Status;
        const fileNameRaw = payload?.fileName ?? payload?.FileName;
        const downloadUrlRaw = payload?.downloadUrl ?? payload?.DownloadUrl;

        const jobId = String(jobIdRaw || '').toLowerCase();
        const status = String(statusRaw || '').trim().toLowerCase();

        if (!jobId || status !== 'completed') return;
        if (processedExportJobsRef.current.has(jobId)) return;
        processedExportJobsRef.current.add(jobId);

        try {
          const downloaded = await runExclusiveExportJobDownload(jobId, async () => {
            const sessionInfo = sessionService.getSessionToken();

            let url = `${process.env.REACT_APP_API_URL}/Item/export-jobs/${jobId}/download`;
            if (typeof downloadUrlRaw === 'string' && downloadUrlRaw.length > 0) {
              if (downloadUrlRaw.startsWith('http')) {
                url = downloadUrlRaw;
              } else {
                const prefix = process.env.REACT_APP_API_URL;
                const separator = downloadUrlRaw.startsWith('/') ? '' : '/';
                url = `${prefix}${separator}${downloadUrlRaw}`;
              }
            }

            const response = await fetch(url, {
              method: 'GET',
              headers: { Authorization: `Bearer ${sessionInfo}` }
            });

            if (!response.ok) {
              throw new Error('Export file is not available');
            }

            const blobData = await response.blob();
            const fileName =
              typeof fileNameRaw === 'string' && fileNameRaw.length > 0 ? fileNameRaw : `Export_${jobId}.xlsx`;

            downloadBlob(blobData, fileName);
            toast.success(`Export ${fileName} downloaded`);
          });

          if (!downloaded) {
            processedExportJobsRef.current.delete(jobId);
          }
        } catch {
          processedExportJobsRef.current.delete(jobId);
          toast.error('Export file is ready but could not be downloaded. Please retry export.');
        }
      }
    };

    hub.subscribe(signalREventHandlers);
    return () => hub.unsubscribe(signalREventHandlers);
  }, [sessionService]);

  const fetchSpecialTags = async () => {
    try {
      api
        .get<ApiResponse<SpecialTag[]>>(`/PickList/special-tags`)
        .then((response) => {
          if (response.isSuccess) {
              const findReviewRequired = response?.data?.find((st) => st.tagKey === SpecialTags.ReviewRequired);
              if(findReviewRequired) {
                dispatch(setDashboardViewReviewRequiredTagId(findReviewRequired.tagId));
                dispatch(updateDashboardListRequest({
                  ...dashboardListRequest,
                  tags: [findReviewRequired.tagId]
                }));
              }
          }
        })
        .catch((error) => {
          console.error("Exception from special tags", error);
        });
    } catch (error: any) {
      console.error(error?.message);
    }
  };

  const onActiveMenuChange = (activeParentMenu, activeSubMenu) => {
    let AppBarTitle = `${activeParentMenu.title}${activeSubMenu ? " > " + activeSubMenu.title : ""}`;
    const permissionPath = !ROUTES_WITH_PERMISSIONS.some(({path, permission}) => {
      return !sessionService.hasPermission(permission) && location.pathname === path
    });
    setBreadCrumbText(permissionPath? AppBarTitle: " ");
  };

  const handleUserManualDownload = async () => {
    try {
      dispatch(enableLoader());
      
      setTimeout(() => {
        // Important: Use the SharePoint *site* base URL (not a `/:f:/r/...` folder-view URL), otherwise `download.aspx` can 404.
        const downloadURL = `${SHAREPOINT_DOCUMENTS_URL}/_layouts/15/download.aspx?SourceUrl=${encodeURIComponent(SHAREPOINT_LEAGO_USER_MANUAL_PATH)}`;

        // This works with SharePoint's authentication cookies
        const link = document.createElement('a');
        link.href = downloadURL;
        link.download = 'LEAGO User Manual.pdf';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        link.remove();
      
        dispatch(disableLoader());
      }, 500);
    } catch (error) {
      dispatch(disableLoader());
      toast.error('Error downloading USER MANUAL DOCUMENT.');
      console.error('Error downloading file:', error);
    }
  };


  return (
    <ThemeProvider theme={defaultTheme}>
      <Box className="layout-box" sx={{ display: "flex" }}>
        <CssBaseline />
        <Drawer className="common-sidebar-drawer" variant="permanent" open={open}>
          <Toolbar
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              px: [1],
            }}
          >
            {open && <div style={{ width: "265px" }}>
              <Link href="/">
                <img
                  src="/logo.png"
                  alt="Visual Comfort"
                  className="img-fluid"
                  style={{ width: "100%", marginTop: "10px" }}
                />
              </Link>
              <h4 style={{marginTop: "5px", marginLeft: "5px"}}>LEAGO</h4>
            </div>}
            <IconButton onClick={toggleDrawer}>
              { open ? <KeyboardDoubleArrowLeftIcon /> : <KeyboardDoubleArrowRightIcon /> }
            </IconButton>
          </Toolbar>
          <Divider />
          <List className={`common-sidebar-list ${open ? "" : "closed-sidebar"}`} component="nav">
            <Sidebar onActiveMenuChange={onActiveMenuChange} open={open} />
          </List>
        </Drawer>
        <div className="parent-container">
          <AppBar
            position="sticky"
            style={{
              background: CSSDefaults.headerBgColor,
              color: CSSDefaults.headerFontColor,
              display: "block",
            }}
          >
            <Toolbar
              sx={{
                pr: "15px", // keep right padding when drawer closed
              }}
            >
              <IconButton
                edge="start"
                color="inherit"
                aria-label="open drawer"
                onClick={toggleDrawer}
                sx={{
                  marginRight: "36px",
                  ...(open && { display: "none" }),
                }}
              >
                <MenuIcon />
              </IconButton>
              <Typography
                component="h1"
                variant="h6"
                noWrap
                sx={{ flexGrow: 1 }}
              >
                {breadCrumbText}
              </Typography>
              <Box className="user-manual-button-container">
                <Button type="button" className="user-manual-button" onClick={() => handleUserManualDownload()}>
                  <Typography component="h6" variant="h6" className="user-manual-button-text">LEAGO User Manual</Typography>
                </Button>
              </Box>
              <ManageNotification />
              <Notifications />
              <IconButton title="Logout" onClick={logout}>
                <LogoutOutlinedIcon sx={{ color: "#484848", mr: 2 }} />
              </IconButton>

              <Typography component="h6" variant="h6" textAlign="right" noWrap>
                {user?.name} <br />
                {user?.email}
              </Typography>
            </Toolbar>
          </AppBar>
          <Box
            component="main"
            sx={{
              backgroundColor: (theme) =>
                theme.palette.mode === "light"
                  ? theme.palette.grey[100]
                  : theme.palette.grey[900],
              flexGrow: 1,
              height: "100vh",
              overflow: "auto",
            }}
          >
            <Container className="main-container" maxWidth={"xl"} sx={{ mt: 2, mb: 2 }}>
              {children}
              <Copyright sx={{ pt: 4 }} />
            </Container>
          </Box>
        </div>
      </Box>
    </ThemeProvider>
  );
};
